/**
 * Hover and keyboard layer for the research notes' build-time SVG charts: a crosshair with a tooltip on line
 * charts, a per-bar tooltip on column charts. Every value is also in each chart's data table, so nothing here
 * gates access to a figure. Tooltip text is built with DOM nodes (never innerHTML), and colour keys use classes,
 * so the strict CSP holds.
 */

// Dependencies
import $ from 'almostnojs';

// SVG namespace.
const NS = 'http://www.w3.org/2000/svg';

/**
 * Format a value the way the chart's axis does.
 *
 * @param {number|null} v - Value.
 * @param {string} f - Format name.
 * @returns {string} Text.
 */
function fmt(v, f) {

    // Missing.
    if (v === undefined || v === null) return '—';

    // By format.
    switch (f) {
        case 'int': return Math.round(v).toLocaleString('en-US');
        case 'dec2': return v.toFixed(2);
        case 'pct': return `${Math.round(v)}%`;
        case 'pct1': return `${v.toFixed(1)}%`;
        case 'compact': return Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
        default: return v.toFixed(1);
    }
}

/**
 * Create an SVG element.
 *
 * @param {string} name - Tag.
 * @param {Object} attrs - Attributes.
 * @returns {SVGElement} Element.
 */
function svgEl(name, attrs) {

    // Element with attributes.
    const node = document.createElementNS(NS, name);
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
    return node;
}

/**
 * Create an HTML element with a class and text.
 *
 * @param {string} tag - Tag.
 * @param {string} [cls] - Class.
 * @param {string} [text] - Text.
 * @returns {HTMLElement} Element.
 */
function el(tag, cls, text) {

    // Element.
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
}

/**
 * Attach the hover layer to one chart.
 *
 * @param {HTMLElement} fig - figure.chart with a data-chart attribute.
 * @returns {void}
 */
function attach(fig) {

    // Chart data.
    let d;
    try { d = JSON.parse(fig.getAttribute('data-chart')); } catch { return; }
    const plot = fig.querySelector('.chart-plot'), svg = plot?.querySelector('svg'), tip = plot?.querySelector('.chart-tip');
    if (!plot || !svg || !tip) return;
    const layer = svg.querySelector('.c-hover') || svg.appendChild(svgEl('g', { class: 'c-hover' }));
    const m = d.m;

    // Pointer position in the chart's own units.
    const toView = (e) => { const r = svg.getBoundingClientRect(); return { x: ((e.clientX - r.left) * d.w) / r.width, y: ((e.clientY - r.top) * d.h) / r.height }; };

    // Place the tooltip beside a point, inside the plot.
    const place = (vx, vy) => {
        const r = svg.getBoundingClientRect(), px = (vx * r.width) / d.w, py = (vy * r.height) / d.h;
        tip.hidden = false;
        const tw = tip.offsetWidth, th = tip.offsetHeight;
        tip.style.left = `${Math.max(0, px + 14 + tw > r.width ? px - tw - 14 : px + 14)}px`;
        tip.style.top = `${Math.max(0, Math.min(r.height - th, py - th / 2))}px`;
    };

    // Clear.
    const hide = () => { tip.hidden = true; layer.replaceChildren(); };

    // Column charts: one tooltip per bar.
    if (d.kind === 'column') {
        let idx = -1;
        const showBar = (i) => {
            idx = i;
            const b = d.bars[i];
            tip.replaceChildren(el('div', 'tt-h', b.label), Object.assign(el('div', 'tt-r'), { textContent: fmt(b.v, d.yFormat) }));
            if (b.note) tip.appendChild(el('div', 'tt-h', b.note));
            place(b.x, m.t + 30);
        };
        $(plot).on('pointermove', (e) => {
            const v = toView(e);
            let best = 0;
            d.bars.forEach((b, i) => { if (Math.abs(b.x - v.x) < Math.abs(d.bars[best].x - v.x)) best = i; });
            showBar(best);
        });
        $(plot).on('pointerleave', hide).on('blur', hide);
        $(plot).on('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); showBar(Math.max(0, Math.min(d.bars.length - 1, idx + (e.key === 'ArrowRight' ? 1 : -1)))); }
            if (e.key === 'Escape') hide();
        });
        return;
    }

    // Line charts need series.
    if (!Array.isArray(d.series)) return;

    // Every x, in order, and the scales.
    const xs = [...new Set(d.series.flatMap((s) => s.points.map((p) => p[0])))].sort((a, b) => a - b);
    const X = d.xScale === 'log'
        ? (v) => { const a = Math.log10(d.xMin), b = Math.log10(d.xMax); return m.l + ((Math.log10(v) - a) / (b - a || 1)) * (d.w - m.r - m.l); }
        : (v) => m.l + ((v - d.xMin) / (d.xMax - d.xMin || 1)) * (d.w - m.r - m.l);
    const Y = (v) => d.h - m.b - ((v - d.yMin) / (d.yMax - d.yMin || 1)) * (d.h - m.b - m.t);
    let cur = -1;

    // Crosshair and tooltip at one x.
    const show = (i) => {
        cur = i;
        const x = xs[i];
        layer.replaceChildren(svgEl('line', { x1: X(x), x2: X(x), y1: m.t, y2: d.h - m.b }));
        const rows = [];
        let topY = d.h;
        d.series.forEach((s) => {
            const pt = s.points.find((p) => p[0] === x);
            if (!pt) return;
            const cy = Y(pt[1]);
            topY = Math.min(topY, cy);
            layer.appendChild(svgEl('circle', { cx: X(x), cy, r: 4.5, class: `s${s.slot}` }));
            const name = el('span');
            name.append(el('i', `key s${s.slot}`), s.name);
            const row = el('div', 'tt-r');
            row.append(name, el('span', '', fmt(pt[1], d.yFormat)));
            rows.push(row);
        });
        tip.replaceChildren(el('div', 'tt-h', `${d.xLabel ? `${d.xLabel} ` : ''}${fmt(x, d.xFormat)}`), ...rows);
        place(X(x), topY);
    };

    // Pointer and keyboard.
    $(plot).on('pointermove', (e) => {
        const v = toView(e);
        let best = 0;
        xs.forEach((x, i) => { if (Math.abs(X(x) - v.x) < Math.abs(X(xs[best]) - v.x)) best = i; });
        if (best !== cur) show(best);
    });
    $(plot).on('pointerleave', () => { cur = -1; hide(); }).on('blur', () => { cur = -1; hide(); });
    $(plot).on('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); show(Math.max(0, Math.min(xs.length - 1, cur < 0 ? 0 : cur + (e.key === 'ArrowRight' ? 1 : -1)))); }
        if (e.key === 'Escape') { cur = -1; hide(); }
    });
}

// Every chart on the page.
$('figure.chart[data-chart]').each((fig) => attach(fig));
